'use client';
import styles from './trial-tab.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faCircleInfo,
	faFilePlus,
	faLocationArrow,
	faLocationDotSlash,
} from '@awesome.me/kit-012d1b9af0/icons/classic/regular';
import { Tabs } from '@chakra-ui/react';
import Overview from '@/components/trial/trial-detail/trial-tab/tabs/overview/overview';
import {
	Collaborators,
	Conditions,
	Detailed,
	Eligibility,
	Enrollment,
	ExtraLink,
	Interventions,
	Keywords,
	Outcomes,
	References,
	Sponsors,
	StudyDocument,
	Summary,
	TrialControlGroup,
} from '@/components/trial/trial-detail/trial-tab/tabs/details/details';
import Map from '@/components/trial/trial-detail/trial-tab/tabs/locations/map/map';
import { useUser } from '@/context/user-context';
import { LocationList } from '@/components/trial/trial-detail/trial-tab/tabs/locations/list/locations';
import { TrialDetails } from '@scitrials-sst/core/Next/types';
import ActionButton from '@/components/action-button/action-button';

const tabListStyle = {
	padding: '7',
	color: '#555',
	fontWeight: 600,
	fontSize: '1.2rem',
	flex: 1,
	justifyContent: 'center',
	alignItems: 'center',
	_selected: {
		backgroundColor: 'rgb(68, 108, 179, 0.1)',
		borderBottom: '2px solid #446cb3',
		color: '#446cb3',
	},
};

const tabContentAnimation = {
	animationName: 'fade-in, scale-in',
	animationDuration: '500ms',
};

export default function TrialTab({
	tab,
	setTab,
	trial,
	applied,
}: {
	tab: 'overview' | 'details' | 'locations';
	setTab: React.Dispatch<
		React.SetStateAction<'overview' | 'details' | 'locations'>
	>;
	trial: TrialDetails;
	applied: string[];
}) {
	const {
		user: { follow },
	} = useUser();

	return (
		<div className={styles['main-tab']}>
			<div className={styles['btn-action']}>
				<ActionButton nctid={trial?.objectID} />
			</div>

			<Tabs.Root
				maxWidth='100%'
				variant='outline'
				value={tab}
				onValueChange={(e) =>
					setTab(e.value as 'overview' | 'details' | 'locations')
				}
			>
				<Tabs.List display={'flex'} justifyContent={'space-between'}>
					<Tabs.Trigger
						value='overview'
						{...tabListStyle}
						aria-label='trial overview'
					>
						<FontAwesomeIcon icon={faCircleInfo} size='lg' />
						<span className={styles['tab-title']}>Trial Overview</span>
					</Tabs.Trigger>
					<Tabs.Trigger
						value='details'
						{...tabListStyle}
						aria-label='trial details'
					>
						<FontAwesomeIcon icon={faFilePlus} size='lg' />
						<span className={styles['tab-title']}>Trial Details</span>
					</Tabs.Trigger>
					<Tabs.Trigger
						value='locations'
						{...tabListStyle}
						aria-label='locations'
					>
						<FontAwesomeIcon icon={faLocationArrow} size='lg' />
						<span className={styles['tab-title']}>Locations</span>
					</Tabs.Trigger>
				</Tabs.List>

				<Tabs.Content value='overview' _open={tabContentAnimation}>
					<Overview
						answers={[
							{ whatIsIt: trial?.curated?.review?.whatIsIt ?? '' },
							{
								whoCanTakePart: trial?.curated?.review?.whoCanTakePart ?? '',
								timeframeInt:
									trial?.curated?.patient_population?.timeframeInt ?? 0,
								levelInt: trial?.curated?.patient_population?.levelInt ?? 0,
								asiaInt: trial?.curated?.patient_population?.asiaInt ?? 0,
								minimumAge: trial?.minimumAge ?? '',
								maximumAge: trial?.maximumAge ?? '',
								sex: trial?.sex ?? '',
							},
							{ whatsInvolved: trial?.curated?.review?.whatsInvolved ?? '' },
							{
								locations: trial?.locations ?? [],
								watch: follow ?? [],
								nctid: trial?.objectID,
							},
							{ pay: trial?.curated?.review?.pay ?? '' },
							{ costs: trial?.curated?.review?.costs ?? '' },
							{
								controlGroup: trial?.curated?.controlGroup ?? '',
								controlGroupFullTreatment:
									trial?.curated?.controlGroupFullTreatment ?? '',
							},
							{ regulation: trial?.curated?.review?.regulation ?? '' },
						]}
						goTo={(value: 'details' | 'locations') => setTab(value)}
					/>
				</Tabs.Content>

				<Tabs.Content value='details' _open={tabContentAnimation}>
					<div className={styles.details}>
						<Summary summary={trial?.briefSummary} />

						<Detailed detail={trial?.detailedDescription} />

						<Eligibility
							eligibility={{
								gender: trial?.sex,
								minAge: trial?.minimumAge,
								maxAge: trial?.maximumAge,
								criteria: trial?.eligibilityCriteria,
								studyPopulation: trial?.studyPopulation,
								samplingMethod: trial?.samplingMethod,
								healthy: trial?.healthyVolunteers,
							}}
						/>

						<Enrollment participant={trial?.enrollmentInfoCount} />

						<Interventions
							armGroups={trial?.armsInterventionsModule?.armGroups ?? []}
							interventions={
								trial?.armsInterventionsModule?.interventions ?? []
							}
						/>

						<Outcomes
							outcomes={{
								primaryOutcomes: trial?.outcomesModule?.primaryOutcomes ?? [],
								secondaryOutcomes:
									trial?.outcomesModule?.secondaryOutcomes ?? [],
							}}
						/>

						<TrialControlGroup
							controlGroup={trial?.curated?.controlGroup}
							controlGroupFullTreatment={
								trial?.curated?.controlGroupFullTreatment
							}
							controlGroupText={trial?.curated?.controlGroupText}
						/>

						<References references={trial?.references ?? []} />

						<Conditions conditions={trial?.conditions ?? []} />

						<Sponsors sponsors={trial?.leadSponsorName} />

						<Collaborators collabs={trial?.collaborators ?? []} />

						<StudyDocument documents={trial?.documents ?? []} />

						<Keywords keys={trial?.keywords ?? []} />

						<ExtraLink links={trial?.seeAlsoLinks ?? []} />
					</div>
				</Tabs.Content>

				<Tabs.Content value='locations' _open={tabContentAnimation}>
					{!trial?.locations || trial?.locations.length === 0 ? (
						<div className={styles.empty}>
							<p>
								<FontAwesomeIcon icon={faLocationDotSlash} /> No facilities
							</p>
						</div>
					) : (
						<>
							<Map
								Marks={
									trial?.locations?.filter(
										(loc) => loc?.geoPoint?.lat && loc?.geoPoint?.lon
									) ?? []
								}
							/>
							<LocationList
								nctid={trial?.objectID}
								data={trial?.locations ?? []}
								applied={applied}
							/>
						</>
					)}
				</Tabs.Content>
			</Tabs.Root>
		</div>
	);
}
